import {
    isDigitalEnvelopeBlockingStep4,
    isPhysicalSignatureMethodLocked,
    resolveCapitalGlobalLockedSignatureMethod,
} from '@/features/proposals/components/capital-global/utils/capital-global-docusign-signature.util';
import {
    isCapitalGlobalProposalEditable,
    shouldOpenCapitalGlobalSignatureStep,
} from '@/features/proposals/components/capital-global/utils/capital-global-proposal-editability.util';
import { useCapitalGlobalDocusignSignatureStatus } from '@/features/proposals/hooks/capital-global/use-capital-global-docusign-signature.hook';
import {
    capitalGlobalProposalSignatureMethodQueryKey,
    useCapitalGlobalProposalSignatureMethod,
} from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-signature-method.hook';
import { useProposalSchema } from '@/features/proposals/hooks/use-proposal-schema.hook';
import type { CapitalGlobalProposalStepDadosDoSeguroValues } from '@/features/proposals/schemas/capital-global/proposal-step-dados-do-seguro.schema';
import { getProposalCategory } from '@/features/proposals/utils/proposal-category';
import type {
    Proposal,
    ProposalCategories,
    ProposalSignatureMethodResponse,
} from '@/services/proposals/types';
import { toaster } from '@/shared/ui/toaster.component';
import { Spinner, Steps, Text } from '@noorden-insurtech/alba-design-system';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ComponentType } from 'react';
import type { CapitalGlobalProposalStepProps } from './capital-global-proposal-step.types';
import { StepCapitalGlobalAssinatura } from './steps/step-capital-global-assinatura.component';
import { StepCapitalGlobalDadosDoSeguro } from './steps/step-capital-global-dados-do-seguro.component';
import { StepCapitalGlobalPagamento } from './steps/step-capital-global-pagamento.component';
import { StepCapitalGlobalPlaceholder } from './steps/step-capital-global-placeholder.component';
import { StepCapitalGlobalResumo } from './steps/step-capital-global-resumo.component';

type Props = {
  proposal: Proposal;
  policyId: string;
  mode: 'edit' | 'view';
};

const STEP_REGISTRY: Record<
  string,
  ComponentType<CapitalGlobalProposalStepProps>
> = {
  '1': StepCapitalGlobalDadosDoSeguro,
  '2': StepCapitalGlobalPagamento,
  '3': StepCapitalGlobalResumo,
  '4': StepCapitalGlobalAssinatura,
};

export function NewCapitalGlobalProposalForm({
  proposal,
  policyId,
  mode,
}: Props) {
  const isViewMode = mode === 'view';
  const trimmedPolicyId = policyId.trim();
  const queryClient = useQueryClient();
  const signatureQuery =
    useCapitalGlobalDocusignSignatureStatus(trimmedPolicyId);
  const signatureMethodQuery =
    useCapitalGlobalProposalSignatureMethod(trimmedPolicyId);
  const signature = signatureQuery.data ?? null;
  const signatureMethod = signatureMethodQuery.data ?? null;
  const lockedSignatureMethod = useMemo(
    () =>
      resolveCapitalGlobalLockedSignatureMethod({
        signatureMethod,
        signature,
      }),
    [signature, signatureMethod]
  );
  const blocksStep4ForDigital = isDigitalEnvelopeBlockingStep4(signature);
  const canAccessStep4 =
    isPhysicalSignatureMethodLocked(lockedSignatureMethod) &&
    !blocksStep4ForDigital;

  const isEditable = isCapitalGlobalProposalEditable({
    proposal,
    signature,
    signatureMethod,
  });
  const effectiveViewMode = isViewMode || !isEditable;
  const openSignatureStep = shouldOpenCapitalGlobalSignatureStep({
    proposal,
    signature,
    signatureMethod,
  });

  const schemaCategory = useMemo((): ProposalCategories | undefined => {
    const code = proposal.productCode;
    if (code == null || code === '') return undefined;
    return getProposalCategory(code);
  }, [proposal.productCode]);

  const { data: schema, isLoading } = useProposalSchema(schemaCategory, {
    enabled: schemaCategory != null,
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSetInitialStep, setHasSetInitialStep] = useState(false);
  const [step1Draft, setStep1Draft] = useState<
    CapitalGlobalProposalStepDadosDoSeguroValues | undefined
  >();

  const steps = schema?.steps ?? [];

  const resumoStepIndex = useMemo(
    () => steps.findIndex(step => step.step === '3'),
    [steps]
  );

  const step4Index = useMemo(
    () => steps.findIndex(step => step.step === '4'),
    [steps]
  );

  useEffect(() => {
    if (!effectiveViewMode || steps.length === 0 || hasSetInitialStep) return;

    const targetIndex = openSignatureStep
      ? resumoStepIndex >= 0
        ? resumoStepIndex
        : steps.length - 1
      : steps.length - 1;

    setCurrentStep(targetIndex >= 0 ? targetIndex : 0);
    setHasSetInitialStep(true);
  }, [
    effectiveViewMode,
    steps.length,
    hasSetInitialStep,
    openSignatureStep,
    resumoStepIndex,
  ]);

  const handleStepClick = (stepIndex: number) => {
    const targetStep = steps[stepIndex];
    const isForwardStepInViewMode =
      effectiveViewMode &&
      stepIndex > currentStep &&
      targetStep?.step === '4' &&
      canAccessStep4;

    if (effectiveViewMode && stepIndex > currentStep && !isForwardStepInViewMode) {
      return;
    }

    if (targetStep?.step === '4') {
      if (blocksStep4ForDigital) {
        toaster.create({
          type: 'info',
          title: 'Assinatura digital em andamento',
          description:
            'Conclua ou cancele a assinatura digital antes de acessar a etapa de upload.',
        });
        return;
      }

      if (!canAccessStep4) {
        toaster.create({
          type: 'info',
          title: 'Assinatura física não escolhida',
          description:
            'Confirme a assinatura física no resumo da proposta antes de acessar o upload.',
        });
        return;
      }
    }

    setCurrentStep(stepIndex);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = steps[currentStep + 1];
      if (nextStep?.step === '4') {
        const currentSignatureMethod =
          queryClient.getQueryData<ProposalSignatureMethodResponse>(
            capitalGlobalProposalSignatureMethodQueryKey(trimmedPolicyId)
          ) ?? signatureMethod;
        const canAccessNextStep =
          isPhysicalSignatureMethodLocked(
            resolveCapitalGlobalLockedSignatureMethod({
              signatureMethod: currentSignatureMethod,
              signature,
            })
          ) && !blocksStep4ForDigital;

        if (blocksStep4ForDigital) {
          toaster.create({
            type: 'info',
            title: 'Assinatura digital em andamento',
            description:
              'Conclua ou cancele a assinatura digital antes de avançar.',
          });
          return;
        }

        if (!canAccessNextStep) {
          toaster.create({
            type: 'info',
            title: 'Assinatura física não escolhida',
            description:
              'Confirme a assinatura física no resumo da proposta antes de avançar.',
          });
          return;
        }
      }
      setCurrentStep(s => s + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(s => s - 1);
    }
  };

  if (isLoading || !steps.length) {
    return (
      <div className='flex items-center justify-center py-16'>
        <div className='flex items-center gap-3'>
          <Spinner size='sm' colorScheme='gray' />
          <Text className='text-sm text-alba-gray-600'>
            Carregando etapas da proposta...
          </Text>
        </div>
      </div>
    );
  }

  const stepMeta = steps[currentStep];
  const canGoBack = currentStep > 0;
  const canGoNext = currentStep < steps.length - 1;
  const StepBody = stepMeta ? STEP_REGISTRY[stepMeta.step] : undefined;

  return (
    <div className='flex flex-col gap-8'>
      <Steps.Root
        step={currentStep}
        count={steps.length}
        linear={!effectiveViewMode}
        onStepChange={details => handleStepClick(details.step)}
      >
        <Steps.List>
          {steps.map((step, index) => (
            <Steps.Item key={step.step} index={index}>
              <Steps.Trigger
                disabled={
                  (effectiveViewMode &&
                    !(index === step4Index && canAccessStep4)) ||
                  !proposal ||
                  (index === step4Index &&
                    (!canAccessStep4 || blocksStep4ForDigital))
                }
              >
                <Steps.Indicator />
                <Steps.Title>{step.stepName}</Steps.Title>
              </Steps.Trigger>
              {index < steps.length - 1 && <Steps.Separator />}
            </Steps.Item>
          ))}
        </Steps.List>
      </Steps.Root>

      <div className='relative isolate w-full rounded-2xl bg-white p-8 shadow-md flex flex-col gap-8'>
        {StepBody ? (
          <StepBody
            proposal={proposal}
            policyId={policyId}
            isViewMode={effectiveViewMode}
            onNext={handleNext}
            onBack={handleBack}
            canGoNext={canGoNext}
            canGoBack={canGoBack}
            step1Draft={step1Draft}
            onStep1DraftChange={setStep1Draft}
          />
        ) : (
          <StepCapitalGlobalPlaceholder
            proposal={proposal}
            policyId={policyId}
            isViewMode={effectiveViewMode}
            stepTitle={stepMeta?.stepName ?? ''}
            stepDescription={stepMeta?.stepDescription}
            onNext={handleNext}
            onBack={handleBack}
            canGoNext={canGoNext}
            canGoBack={canGoBack}
          />
        )}
      </div>
    </div>
  );
}
